import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
import PropTypes from 'prop-types';
import { Fragment, Component } from 'react';
import { omit, debounce } from '../../../utils';
import FormattedMessage from 'I18n/components/FormattedMessage';
import * as tracker from 'ui-addon-upgrades/_core/common/eventTracking/tracker';
import { upgradeDataPropsInterface } from 'ui-addon-upgrades/_core/common/data/upgradeData/interfaces/upgradeDataPropsInterface';
import { TrialActivationButtonPropsInterface } from 'ui-addon-upgrades/_core/trial/TrialPropsInterface';
import AdminOnlyWrapper from 'ui-addon-upgrades/_core/common/components/AdminOnlyWrapper';
import { TEAM } from 'ui-addon-upgrades/_core/common/constants/AdminTypes';
import { REQUEST_TRIAL } from 'ui-addon-upgrades/_core/common/constants/AdminModalTypes';
import TrialActivationModalContainer from './TrialActivationModalContainer';
import UILoadingButton from 'UIComponents/button/UILoadingButton';
import UIButton from 'UIComponents/button/UIButton';
import { getUpgradeDataTrackingProperties } from 'ui-addon-upgrades/ums/utils';
import { TRIAL_STANDARD_DURATION, TX111_TRIAL_DURATION } from '../constants';
const CLICK_DELAY = 50;

function sequence(...handlers) {
  return (...args) => {
    handlers.forEach(handler => handler && handler(...args));
  };
}

class TrialActivationButton extends Component {
  constructor(props) {
    super(props);

    this.handleClick = () => {
      const {
        activeTrials,
        expiredTrials,
        isMultiTrial,
        upgradeData,
        isUpgradePoint,
        level
      } = this.props;
      tracker.track('trialActivationButtonInteraction', Object.assign({
        action: 'clicked'
      }, getUpgradeDataTrackingProperties(upgradeData, isUpgradePoint), {
        isMultiTrial,
        activeTrials,
        expiredTrials,
        trialId: upgradeData.trialId,
        duration: upgradeData.duration
      }));

      if (isUpgradePoint) {
        tracker.track('interaction', Object.assign({
          action: 'clicked upgrade point'
        }, getUpgradeDataTrackingProperties(upgradeData, isUpgradePoint)));
        tracker.track('interaction', Object.assign({
          action: 'clicked trial activation',
          isPrimaryCta: level === 1,
          isSecondaryCta: level === 2
        }, getUpgradeDataTrackingProperties(upgradeData, isUpgradePoint)));
      }

      this.setState({
        showActivationModalContainer: true
      });
    };

    this.state = {
      showActivationModalContainer: false
    };
    this.debouncedHandleClick = debounce(this.handleClick, CLICK_DELAY);
  }

  componentDidMount() {
    const {
      activeTrials,
      expiredTrials,
      isMultiTrial,
      upgradeData,
      isUpgradePoint,
      level
    } = this.props;
    tracker.track('trialActivationButtonInteraction', Object.assign({
      action: 'viewed'
    }, getUpgradeDataTrackingProperties(upgradeData, isUpgradePoint), {
      isMultiTrial,
      activeTrials,
      expiredTrials,
      trialId: upgradeData.trialId,
      duration: upgradeData.duration
    }));

    if (isUpgradePoint) {
      tracker.track('interaction', Object.assign({
        action: 'viewed trial activation',
        isPrimaryCta: level === 1,
        isSecondaryCta: level === 2
      }, getUpgradeDataTrackingProperties(upgradeData, isUpgradePoint)));
    }
  }

  render() {
    const {
      activeTrials,
      expiredTrials,
      hideBackground,
      isMultiTrial,
      upgradeData: {
        trialId,
        upgradeProduct,
        duration
      },
      upgradeData,
      onClick = () => {},
      onSuccess,
      shouldHideSurvey,
      successModalHeader,
      successModalSubText,
      successModalCtaText,
      showSuccessModalIllustration,
      children
    } = this.props; // TX111

    const isExtendedTrial = duration === TX111_TRIAL_DURATION;
    const {
      showActivationModalContainer
    } = this.state;
    const onClickSequence = sequence(this.debouncedHandleClick, onClick);
    const propsToPass = omit(this.props, ['onClick', 'upgradeData', 'onSuccess', 'shouldHideSurvey', 'trialId', 'allowModal', 'successModalHeader', 'successModalSubText', 'successModalCtaText', 'showSuccessModalIllustration', 'isMultiTrial', 'activeTrials', 'expiredTrials', 'isUpgradePoint', 'hideBackground']);
    if (!trialId) return null;
    return /*#__PURE__*/_jsxs(Fragment, {
      children: [showActivationModalContainer && /*#__PURE__*/_jsx(TrialActivationModalContainer, {
        activeTrials: activeTrials,
        expiredTrials: expiredTrials,
        hideBackground: hideBackground,
        isMultiTrial: isMultiTrial,
        upgradeData: upgradeData,
        shouldHideSurvey: shouldHideSurvey,
        onSuccess: onSuccess,
        successModalHeader: successModalHeader,
        successModalSubText: successModalSubText,
        successModalCtaText: successModalCtaText,
        showSuccessModalIllustration: showSuccessModalIllustration
      }), /*#__PURE__*/_jsx(AdminOnlyWrapper, {
        upgradeData: upgradeData,
        adminType: TEAM,
        modalType: REQUEST_TRIAL,
        children: propsToPass.use && propsToPass.use.includes('link') ? /*#__PURE__*/_jsx(UIButton, Object.assign({}, propsToPass, {
          onClick: onClickSequence,
          "data-selenium": "trial-button",
          "data-reagan-primary-cta": true,
          children: children || /*#__PURE__*/_jsx(FormattedMessage, {
            message: isExtendedTrial ? `upgrades.trialActivationButton.tx111DynamicDuration` : `upgrades.trialActivationButton.${upgradeProduct}`,
            options: {
              duration: duration || TRIAL_STANDARD_DURATION
            }
          })
        })) : /*#__PURE__*/_jsx(UILoadingButton, Object.assign({}, propsToPass, {
          onClick: onClickSequence,
          "data-selenium": "trial-button",
          "data-reagan-primary-cta": true,
          loading: showActivationModalContainer,
          children: children || /*#__PURE__*/_jsx(FormattedMessage, {
            message: isExtendedTrial ? `upgrades.trialActivationButton.tx111DynamicDuration` : `upgrades.trialActivationButton.${upgradeProduct}`,
            options: {
              duration: duration || TRIAL_STANDARD_DURATION
            }
          })
        }))
      })]
    });
  }

}

TrialActivationButton.defaultProps = {
  use: 'primary',
  onClick: () => {}
}; // @ts-expect-error type is too long

TrialActivationButton.propTypes = Object.assign({}, upgradeDataPropsInterface, {}, TrialActivationButtonPropsInterface, {
  activeTrials: PropTypes.array.isRequired,
  expiredTrials: PropTypes.array.isRequired,
  children: PropTypes.node,
  isMultiTrial: PropTypes.bool.isRequired,
  onClick: PropTypes.func
});
export default TrialActivationButton;